import { createReducer } from '../lib/createReducer.js';
import { mergeByProp } from '@shared/utils/mergeByProp.js';
import getEmbeddedApiConfigurationState from '@/lib/getEmbeddedApiConfigurationState.js';
import mergeIntegrationPreservingApiConfiguration from './embeddedReducer/mergeIntegrationPreservingApiConfiguration.js';

const initialState = {
  companyId: null,
  integrations: [],
  provisionedCredential: null,
  hasFetchedMetadata: false,
  metadataRequestId: null,
  metadataFailed: false,
  integrationFailures: {},
  integrationDetailsLoaded: {},
  integrationRequestIds: {},
  credentialExchangeCustomAuthFields: []
};

export default createReducer(initialState, {
  SET_EMBEDDED_COMPANY: (state, companyId) => (state.companyId === companyId ? state : { ...initialState, companyId }),

  SET_SESSION_COMPANY_ID: (state, companyId) => (state.companyId === companyId ? state : { ...initialState, companyId }),

  LOGOUT: () => initialState,
  GET_INTEGRATIONS_METADATA_START: (state, payload) => (payload.companyId === state.companyId ? { ...state, metadataRequestId: payload.requestId, metadataFailed: false } : state),
  // The metadata list is authoritative for membership and order, but its rows are
  // slim (no workflows/endUsers/apps/settings). Replacing the array wholesale here
  // wipes any integration detail a concurrent GET_INTEGRATION already merged, so
  // fold existing rows in under the fresh metadata instead.
  GET_INTEGRATIONS_METADATA_SUCCESS: (state, payload) => {
    if (payload?.meta?.companyId !== state.companyId || payload.meta.requestId !== state.metadataRequestId) {
      return state;
    }
    return {
      ...state,
      integrations: (payload?.data || []).map((incoming) => {
        const existing = state.integrations.find((integration) => integration?.id === incoming?.id);
        return existing ? { ...existing, ...incoming } : incoming;
      }),
      hasFetchedMetadata: true,
      metadataRequestId: null,
      metadataFailed: false
    };
  },

  GET_INTEGRATIONS_METADATA_FAIL: (state, payload) => (payload?.meta?.companyId === state.companyId && payload.meta.requestId === state.metadataRequestId ? { ...state, hasFetchedMetadata: true, metadataRequestId: null, metadataFailed: true } : state),

  CREATE_INTEGRATION_SUCCESS: (state, payload) =>
    payload?.data?.companyId === state.companyId
      ? {
          ...state,
          metadataRequestId: null,
          integrationRequestIds: { ...state.integrationRequestIds, [payload.data.id]: null },
          integrations: mergeByProp('id', state.integrations, [payload.data], (existing, incoming) => ({ ...existing, ...incoming }))
        }
      : state,

  SET_EMBEDDED_API_CONFIGURATION_SUCCESS: (state, payload) => {
    if (payload?.meta?.companyId !== state.companyId || payload?.data?.companyId !== state.companyId || payload?.data?.id !== payload?.meta?.integrationId || typeof payload?.meta?.apiConfigured !== 'boolean' || getEmbeddedApiConfigurationState(payload.data) !== (payload.meta.apiConfigured ? 'configured' : 'automatic')) {
      return state;
    }
    return {
      ...state,
      metadataRequestId: null,
      integrationRequestIds: { ...state.integrationRequestIds, [payload.data.id]: null },
      integrations: mergeByProp('id', state.integrations, [payload.data], (existing, incoming) => ({ ...existing, ...incoming }))
    };
  },

  UPDATE_INTEGRATION_SUCCESS: (state, payload) =>
    payload?.data?.companyId === state.companyId
      ? {
          ...state,
          integrations: mergeByProp('id', state.integrations, [payload?.data], mergeIntegrationPreservingApiConfiguration)
        }
      : state,

  DELETE_INTEGRATION_SUCCESS: (state, payload) => ({
    ...state,
    integrations: state.integrations.filter((integration) => integration.id !== payload?.data?.id)
  }),

  GET_INTEGRATION_START: (state, payload) => (payload.companyId === state.companyId ? { ...state, integrationRequestIds: { ...state.integrationRequestIds, [payload.integrationId]: payload.requestId } } : state),

  GET_INTEGRATION_SUCCESS: (state, payload) => {
    if (payload?.meta?.companyId !== state.companyId || !payload.meta.requestId || payload.meta.requestId !== state.integrationRequestIds[payload.meta.integrationId]) {
      return state;
    }
    if (payload?.data?.companyId !== state.companyId || payload?.data?.id !== payload.meta.integrationId) {
      return { ...state, integrationRequestIds: { ...state.integrationRequestIds, [payload.meta.integrationId]: null }, integrationFailures: { ...state.integrationFailures, [payload.meta.integrationId]: true } };
    }
    const integrationFailures = { ...state.integrationFailures };
    delete integrationFailures[payload.meta.integrationId];
    return {
      ...state,
      integrationFailures,
      integrationRequestIds: { ...state.integrationRequestIds, [payload.meta.integrationId]: null },
      integrationDetailsLoaded: { ...state.integrationDetailsLoaded, [payload.meta.integrationId]: true },
      integrations: mergeByProp('id', state.integrations, [payload?.data], (existing, incoming) => ({
        ...existing,
        ...incoming
      }))
    };
  },

  GET_INTEGRATION_FAIL: (state, payload) => (payload?.meta?.companyId === state.companyId && payload.meta.requestId && payload.meta.requestId === state.integrationRequestIds[payload.meta.integrationId] ? { ...state, integrationRequestIds: { ...state.integrationRequestIds, [payload.meta.integrationId]: null }, integrationFailures: { ...state.integrationFailures, [payload.meta.integrationId]: true } } : state),

  GET_INTEGRATION_WORKFLOWS_SUCCESS: (state, payload) => ({
    ...state,
    integrations: mergeByProp(
      'id',
      state.integrations,
      [
        {
          id: payload?.meta?.integrationId,
          workflows: payload?.data || []
        }
      ],
      (existing, incoming) => ({
        ...existing,
        ...incoming
      })
    )
  }),

  REMOVE_WORKFLOW_FROM_INTEGRATION_SUCCESS: (state, payload) => ({
    ...state,
    integrations: state.integrations.map((integration) => {
      if (integration.id === payload?.data?.integrationId) {
        return {
          ...integration,
          workflows: (integration.workflows || []).filter((workflow) => workflow.id !== payload?.data?.workflowId)
        };
      }
      return integration;
    })
  }),

  RENAME_INTEGRATION_APP_SUCCESS: (state, payload) => ({
    ...state,
    integrations: state.integrations.map((integration) => {
      if (integration.id === payload?.meta?.integrationId) {
        return { ...integration, apps: payload?.data?.apps || [] };
      }
      return integration;
    })
  }),

  DELETE_INTEGRATION_APP_SUCCESS: (state, payload) => ({
    ...state,
    integrations: state.integrations.map((integration) => {
      if (integration.id === payload?.meta?.integrationId) {
        return { ...integration, apps: payload?.data?.apps || [] };
      }
      return integration;
    })
  }),

  DELETE_INSTALLATION_SUCCESS: (state, payload) => ({
    ...state,
    integrations: state.integrations.map((integration) => {
      if (integration.id === payload?.meta?.integrationId) {
        return {
          ...integration,
          endUsers: (integration.endUsers || []).filter((endUser) => endUser.id !== payload?.meta?.installationId)
        };
      }
      return integration;
    })
  }),

  GET_WORKFLOW_STATE_SUCCESS: (state, payload) => {
    if (!payload?.data?.integration || payload?.meta?.companyId !== state.companyId || payload.data.integration.companyId !== payload.meta.companyId) {
      return state;
    }

    return {
      ...state,
      integrations: mergeByProp('id', state.integrations, [payload.data.integration], mergeIntegrationPreservingApiConfiguration)
    };
  },

  UPSERT_PROVISIONED_CREDENTIAL_SUCCESS: (state, payload) => (payload?.meta?.companyId === state.companyId ? { ...state, provisionedCredential: payload?.data || null } : state),

  UPSERT_PROVISIONED_CREDENTIAL_FAIL: (state, payload) => (payload?.meta?.companyId === state.companyId ? { ...state, provisionedCredential: null } : state),

  SET_CREDENTIAL_EXCHANGE_CUSTOM_AUTH_FIELDS: (state, payload) => (payload?.companyId === state.companyId ? { ...state, credentialExchangeCustomAuthFields: payload.fields || [] } : state),

  UPDATE_INSTALLATION_METADATA_SUCCESS: (state, payload) => ({
    ...state,
    integrations: state.integrations.map((integration) =>
      integration.endUsers?.some((u) => u.id === payload?.meta?.installationId)
        ? {
            ...integration,
            endUsers: (integration.endUsers || []).map((endUser) => (endUser.id === payload?.meta?.installationId ? { ...endUser, metadata: payload?.data?.metadata || payload?.meta?.metadata || {} } : endUser))
          }
        : integration
    )
  })
});
